import { MatterAssignmentDto, MatterAssignmentMinDto } from './assignments';
import { CurrencyData } from './currency';
import { EmployeeListItemMinDto, EmploymentBasicDto } from './employees';
import { ExpenseCreateFromTimecardDto, ExpenseDto, ExpenseMapList } from './expenses';
import { InvoiceMinDto, TimecardExportInvoiceDto } from './invoices';
import { MatterListItem, MatterListItemMin, RemunerationCalcType } from './matters';
import { MinDto, NamedEntityMinDto } from './other';

export interface TimecardsTotalsDto {
    totalUsedTimeInSeconds: number;
    totalBilledTimeInSeconds: number;
    totalFeesInBaseCurrency: number;
    baseCurrencyTypeCode: string;
}

/**
 * One group's totals over the WHOLE filtered set, from `GET /v1/timecards/group-totals`.
 *
 * The list pages 50 rows at a time, so a ribbon that added up the rows it holds would report a partial
 * sum and grow as the reader scrolled into the group. `groupKey` is what `timecardGroupKeyOf` derives
 * from a row, so the two sides match up without either re-deriving the other's.
 */
export interface TimecardGroupTotalsDto {
    groupKey: string;
    loggedTimeInHours: number;
    billedTimeInHours: number;
    totalAmountInBaseCurrency: number;
    /** Fee sums by the rows' own currency; a currency with no billable fee is absent. */
    feesByCurrency: Record<string, number>;
}

export interface TimecardListExportItemDto {
    id: number;
    employee: MinDto;
    matter: MatterListItemMin;
    languageTypeCode: string;
    timecardDate: string;
    createdAt: string | null;
    description: string;
    timecardStatusTypeCode: string;
    timecardActivityTypeCode: string;
    totalTimeInSeconds: number;
    billedTimeInSeconds: number;
    amountInOriginalCurrency: number;
    isNonBillable: boolean;
    preserveNonBillableHours: boolean;
    practiceAreas: MinDto[];
    matterAssignment: MinDto | null;
    multiplierRate: number;
    employment: EmploymentBasicDto | null;
    invoice: TimecardExportInvoiceDto | null;
    invoiceTimecardDetails: TimecardExportInvoiceTimecardDetails | null;
    billingUpliftValueFactors: string[] | null;
}

export interface TimecardSummarizedStats {
    totalBillableTimeInSeconds: number;
    totalBillableAmountInOriginalCurrency: number;
    uninvoicedTimeInSeconds: number;
    uninvoicedAmountInOriginalCurrency: number;
    invoicedTimeInSeconds: number;
    invoicedAmountInOriginalCurrency: number;
}

export interface TimecardListItemData {
    id: number;
    /** The author, plus the employment whose office the list's "author's office" column shows. */
    employee: NamedEntityMinDto & Pick<EmployeeListItemMinDto, 'latestEmployment'>;
    /** Who entered the timecard, and so sees it among their drafts; absent on timecards whose creator was not recorded. */
    createdByEmployee?: MinDto;
    matter: MatterListItem;
    languageTypeCode: string;
    timecardDate: string;
    createdAt?: string;
    description: string;
    timecardStatusTypeCode: string;
    isStopwatchRunning: boolean;
    totalTimeInSeconds: number;
    isNonBillable: boolean;
    billedTimeInSeconds: number;
    timecardStats?: TimecardStats;
    currencyDetails?: CurrencyData;
    practiceAreas: NamedEntityMinDto[];
    /** The case's practice areas — an optional column, absent from older cached responses. */
    matterPracticeAreas?: NamedEntityMinDto[];
    /** The author's practice areas — likewise. */
    employeePracticeAreas?: NamedEntityMinDto[];
    /**
     * The invoices this timecard is billed on, newest first. Narrowed to what the Invoice column
     * renders: the backend sends `InvoiceMinDto`s here, but populates only a handful of their fields,
     * so declaring the full shape would promise data the list never carries. `TimecardDetails`
     * re-declares it as the full DTO, which its own endpoint does fill.
     */
    invoices?: Pick<InvoiceMinDto, 'id' | 'invoiceNumber' | 'invoiceStatusTypeCode'>[];
    isDeletable: boolean;
    isEditable: boolean;
    isRestricted?: boolean;
    matterAssignment: MatterAssignmentDto;
    timecardActivityTypeCode: string;
    /** Why the current user could not finish this draft as stored; empty when it is not a draft. */
    finishBlockers: FinishBlocker[];
}

/** A rule the backend says a draft fails, which would refuse its finish. */
export type FinishBlocker =
    | 'NO_HOURS'
    | 'OVER_24_HOURS'
    | 'NO_ACTIVITY_TYPE'
    | 'NO_ASSIGNMENT'
    | 'NO_PRACTICE_AREA'
    | 'DATE_LOCKED'
    | 'BEFORE_EMPLOYMENT_START'
    | 'CLIENT_PERSONA_NON_GRATA'
    | 'NO_DESCRIPTION';

export enum TimecardStatusType {
    DRAFT = 'DRAFT',
    DRAFT_INVOICE = 'DRAFT_INVOICE',
    INVOICED = 'INVOICED',
    PAID = 'PAID',
    FINISHED = 'FINISHED',
    DONE = 'DONE',
}

export interface TimecardDto {
    id: number | null;
    description: string;
    isNonBillable: boolean;
    matter: MatterListItemMin | null;
    employee: NamedEntityMinDto | null;
    timecardDate: string | null;
    timecardDateUtcTimestamp?: string;
    multiplierTypeCode: string;
    multiplierRate: string;
    practiceAreas: {
        id: number;
        name: string;
    }[];
    totalTimeInSeconds: number | null;
    totalTimeInHours?: string | null;
    billedTimeInSeconds: number | null;
    billedTimeInHours?: string | null;
    expenses?: ExpenseDto[] | ExpenseCreateFromTimecardDto[] | null;
    matterAssignment?: MatterAssignmentMinDto | null;
    timecardActivityType?: { label: string; value: string } | null;
    remunerationCalcType?: RemunerationCalcType;
    remunerationTimeInSeconds: number | null;
    remunerationTimeInHours?: string | null;
    preserveNonBillableHours: boolean;
    employees?: NamedEntityMinDto[];
    billingUpliftValueFactors?: { label: string; value: string }[] | null;
    valueFactorsNotes?: string | null;
    notes?: string | null;
}

export interface SwitchTimecardsMatter {
    currentMatterId: number;
    newMatterId: number;
    newMatterAssignmentId: number | null;
    timecardIds: number[];
}

export interface UpdateTimecardsBillability {
    matterId: number;
    timecardIds: number[];
    isNonBillable: boolean;
}

export interface TimecardCreateUpdateDto {
    id: number | null;
    description: string;
    isNonBillable: boolean;
    matter: MatterListItemMin | null;
    employee: NamedEntityMinDto | null;
    timecardDate: string | null;
    timecardDateUtcTimestamp?: string;
    multiplierTypeCode: string;
    multiplierRate: string;
    practiceAreas: {
        id: number;
        name: string;
    }[];
    totalTimeInSeconds: number | null;
    totalTimeInHours?: string | null;
    billedTimeInSeconds: number | null;
    billedTimeInHours?: string | null;
    expenses?: ExpenseDto[] | ExpenseCreateFromTimecardDto[] | null;
    matterAssignment: MatterAssignmentMinDto | null;
    timecardActivityTypeCode: string | null;
    remunerationCalcTypeCode: string | null;
    remunerationTimeInSeconds: number | null;
    preserveNonBillableHours: boolean;
    employees?: NamedEntityMinDto[];
    billingUpliftValueFactors?: string[] | null;
    valueFactorsNotes?: string | null;
    notes?: string | null;
}

export interface TimecardDetails extends TimecardListItemData {
    practiceAreas: { id: number; name: string }[];
    multiplierTypeCode: string;
    multiplierRate: string;
    lastStopwatchIntervalInSeconds: number;
    stopwatchRunningSince: string | null;
    timerLogs?: TimerLog[];
    expenses?: ExpenseDto[] | null;
    remunerationCalcTypeCode: RemunerationCalcType;
    remunerationTimeInSeconds?: number;
    preserveNonBillableHours: boolean;
    lastModifiedAt?: string | Date;
    changedByEmployee?: MinDto;
    invoices?: InvoiceMinDto[];
    billingUpliftValueFactors?: string[] | null;
    valueFactorsNotes?: string | null;
    notes?: string | null;
}

export interface TimerLog {
    startedAt: string;
    stoppedAt: string;
}

export interface TimecardStoreProps {
    trackedTimecardId: string | null | undefined;
    isStopwatchRunning: boolean;
    draftTimecardsLoading: boolean;
    timecardDuration: string | undefined;
}
interface TimecardStats {
    originalCurrencyTypeCode?: string;
    baseCurrencyTypeCode?: string;
    totalTimeInSeconds?: number;
    billedTimeInSeconds?: number;
    totalTimeInHours?: number;
    billedTimeInHours?: number;
    amountInOriginalCurrency?: number;
    amountInBaseCurrency?: number;
    currenthourlyPrice?: number;
    expenseList?: ExpenseMapList;
}

export interface TimecardBasicDto {
    id: number;
    matterId: number;
    matter: MatterListItemMin;
    isNonBillable: boolean;
    preserveNonBillableHours: boolean;
    timecardDate: string;
    employee: EmployeeListItemMinDto;
    description?: string;
    totalTimeInSeconds: number;
    billedTimeInSeconds: number;
    timecardActivityTypeCode?: string;
}

interface TimecardExportInvoiceTimecardDetails {
    id: number;
    billedTimeInSeconds: number;
    totalAmount: number;
    amountInPaymentCurrency: number;
    amountInBaseCurrency: number;
    hourlyPrice: number;
    originalCurrencyTypeCode: string;
    paymentCurrencyTypeCode: string;
    employee: MinDto;
    description: string;
}

/**
 * Minimal type for matter timecard history endpoint.
 * Only contains fields needed by TimecardHistoryButton.
 * Does NOT include matter details to prevent leaking sensitive information.
 */
export interface TimecardMatterHistoryItem {
    id: number;
    timecardDate: string;
    employee: MinDto;
    totalTimeInSeconds: number | null;
    description: string | null;
    isRestricted: boolean;
}

/**
 * A finding against a timecard-writing rule: its code (G1 to G12, or the firm's F codes) and a plain sentence
 * without the code, or for what a thin text lacks a phrase that completes "Add:".
 */
export interface DescriptionRule {
    code: string | null;
    reason: string | null;
}

/** A better wording of a timecard description from the AI provider, and the rules it fixes. Nothing is stored. */
export interface ImprovedTimecardDescription {
    /** Null when the text is too thin to improve or breaks no rule. */
    proposal: string | null;
    fixes: DescriptionRule[];
    /** What a text too thin to improve lacks, by the rule that asks for it. */
    missing: DescriptionRule[];
    /** What the proposal cannot fix and the user should still do, such as a split. */
    alsoConsider: DescriptionRule[];
    /** Whether the firm has its own timecard rules, which the text was checked against too. */
    firmRules: boolean;
}

/** The timecard description rules as stored, in markdown: the generic ones, and the firm's own when it has any. */
export interface TimecardDescriptionRules {
    generic: string;
    firm: string | null;
}

/**
 * A reusable description text offered on the timecard form, configured per case type in Back Office.
 * `langCode` is the template's own language — matched against the case's `timecardLanguageCode`, not
 * against the UI locale. Validity dates are ISO `yyyy-MM-dd` and open-ended on both sides.
 */
export interface TimecardTemplateDto {
    id: number;
    langCode: string;
    matterTypeCode: string;
    templateText: string;
    validFrom: string | null;
    validUntil: string | null;
}

export type TimecardTemplateSaveDto = Omit<TimecardTemplateDto, 'id'>;
